/**
 * Provider keys — vendor credentials and per-provider activation.
 *
 * The sibling of Providers & failover, and a different question. That page is *routing*:
 * given several PSPs that can all move naira, which order does failover try them in. This
 * page is *activation*: is a vendor configured at all, and is the operator willing to send
 * real money through it yet. Activation used to be one global `PROVIDER_MODE` env switch,
 * which meant crypto custody could not go live without pointing every naira rail at
 * production in the same breath. It is now per vendor, held in the database, and written
 * from here rather than from a `.env` file an operator cannot reach.
 *
 * ## Why this is a list of rows and not a wall of cards
 *
 * It used to render every vendor as a card with every one of its fields expanded inline —
 * sixteen vendors, some fifty inputs, all on screen at once. The cost was not aesthetic. The
 * page's actual job is to answer "what is live, and what is switched on but broken?", and
 * that answer was buried under fifty text boxes belonging to vendors nobody was touching.
 * Worse, every one of those boxes was a live control: a stray click and a keystroke in a
 * collapsed-looking form could clear a production key. So the board is now compact and
 * read-only — one row per vendor, its state legible at a glance — and every write happens in
 * a focused panel opened deliberately for one vendor at a time.
 *
 * ## Two rules that shape every control below
 *
 * **A secret never comes back.** A field the contract marks `secret` arrives with `value:
 * null` and a `masked` last-four, and that mask is the only evidence this page will ever
 * have. So its input starts empty and stays empty; leaving it blank keeps what is stored,
 * and *removing* a stored secret needs an explicit Clear — because an operator who typed
 * nothing did not ask to wipe the key. That is also why the save sends a sparse patch: only
 * the fields actually edited, never the whole form.
 *
 * **`enabled` is not `live`.** A switch flipped on over a missing required key changes
 * nothing on the money path, and an operator who saw the switch move and assumed the rail
 * was hot is the failure mode this page exists to prevent. Every row says which of the two
 * it is, and the strip at the top says the same thing per capability.
 *
 * Reads need `providers:read`; every control is disabled without `providers:write`.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { API_BASE_URL, USING_MOCK } from '../api/config.js';
import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import { API_PREFIX } from '../contracts/routes.js';
import type {
  ProviderEgress,
  ProviderCredentialField,
  ProviderService,
  ProviderServiceState,
  ProviderStatus,
  ProviderStatusResponse,
  ProviderWebhook,
  SetProviderCredentialsRequest,
} from '../contracts/types.js';
import {
  custodyRequiresVerification,
  readProviderServiceStates,
  type PendingProviderServiceState,
  type PendingSetProviderServicesRequest,
} from '../contracts/pending.js';
import {
  Badge,
  Button,
  CopyButton,
  ErrorBox,
  LoadingState,
  SearchInput,
  Switch,
} from '../components/ui.js';
import { IconAlert, IconChevronDown, IconChevronUp, IconEye } from '../components/icons.js';
import { SecretInput } from '../components/SecretInput.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { relativeTime } from '../lib/format.js';

/**
 * What each capability is called on this page.
 *
 * `Record<ProviderService, string>` and not a partial one, deliberately: the type is the guard.
 * A capability added to `PROVIDER_SERVICES` and not to this literal is a **build error**, which
 * is how `liquidity` was caught within minutes of landing. That is the good direction for this
 * particular failure, and it is worth stating because the instinct on seeing a red build is to
 * loosen the type rather than to fill in the key.
 *
 * Deriving these from the slug was considered and rejected. `humanize('liquidity')` gives
 * "Liquidity", which is the word an operator would have to already understand to read; the
 * whole value of this map is that it says "Buying and selling crypto" instead. A derived map
 * would trade a compile error for a page full of jargon nobody can act on.
 */
const SERVICE_LABELS: Readonly<Record<ProviderService, string>> = {
  payout: 'Naira payout',
  collection: 'Naira collection',
  biller: 'Bills & airtime',
  custody: 'Crypto custody',
  kyc: 'Identity (KYC)',
  sms: 'SMS',
  email: 'Email',
  rates: 'Market rates',
  push: 'Push',
  /*
   * Not "liquidity". An operator reading a board of capabilities needs to know what breaks if
   * this one is off, and the answer is that customers cannot buy or sell coin at a firm price.
   *
   * It reads next to "Market rates" and the two are easy to confuse, which is exactly why the
   * label has to do work: market rates is a *price we were told*, and this is a *price somebody
   * will honour*. A deployment can run one, the other or both, so the two words must not be
   * interchangeable on screen.
   */
  liquidity: 'Buying and selling crypto',
};

/**
 * The capabilities where a live vendor means customer money actually moves.
 *
 * Mirrors the rails the ledger settles against. A mistake on one of these is not a degraded
 * feature, it is a real transfer to a real account, so a vendor serving one of them carries a
 * standing warning once it is live in production.
 */
const MONEY_SERVICES: readonly ProviderService[] = [
  'payout',
  'collection',
  'custody',
  'biller',
  /*
   * Buying and selling belongs here and it is the newest and least obvious member.
   *
   * A market-rate feed only ever *tells* us a number; nothing settles because of it. A firm
   * quote is executed: the platform's coin and the platform's naira actually change hands at a
   * venue, against a quote with an id and an expiry. A misconfiguration there is a real trade
   * at a real price, not a screen showing a stale figure, so it earns the standing warning.
   */
  'liquidity',
];

/**
 * One line per capability, used to build a hint for a combination nobody wrote one for.
 *
 * `Record<ProviderService, string>` for the same reason `SERVICE_LABELS` is: adding a
 * capability without describing it should stop the build, not produce a blank line on a page an
 * operator uses to decide whether a rail is safe to switch on.
 */
const SERVICE_COPY: Readonly<Record<ProviderService, string>> = {
  payout: 'Naira out to bank accounts.',
  collection: 'Naira in by card and virtual account.',
  biller: 'Airtime, data, electricity and TV top-ups.',
  custody: 'Crypto deposit addresses, deposit detection and send-outs.',
  kyc: 'Identity verification behind every tier upgrade.',
  sms: 'Outbound SMS, the channel one-time codes are delivered on.',
  email: 'Receipts, codes by email, support mirrors and operator notifications.',
  rates: 'The market price feed every quote is derived from.',
  push: 'Push notification delivery to the mobile app.',
  liquidity: 'Buying and selling coin at a firm quoted price.',
};

/** Keyed by the group's joined service list, so a two-capability vendor gets its own group. */
const GROUP_COPY: Readonly<Record<string, string>> = {
  'payout+collection':
    'The naira rails: money out to bank accounts and money in by card and virtual account. Ordering between them is set on Providers & failover.',
  biller: 'Airtime, data, electricity and TV top-ups.',
  custody: 'Crypto deposit addresses, deposit detection and send-outs.',
  kyc: 'Identity verification: the BVN and NIN checks behind every tier upgrade.',
  sms: 'Outbound SMS, the channel one-time codes are delivered on.',
  email:
    'Receipts, codes by email, support mirrors and operator notifications. Two ways to send the same messages: an email API, or plain SMTP on your own mailbox. Switch on one of them, not both.',
  rates: 'The market price feed every quote is derived from.',
  push: 'Push notification delivery to the mobile app.',
  liquidity:
    'Where the platform actually buys and sells coin, at a price the venue will honour rather than one it merely reports.',
};

/**
 * The one line under a group heading, for a group that has no hand-written entry.
 *
 * `GROUP_COPY` is keyed on the joined capability list, so it can only ever describe the exact
 * combinations somebody has written down. A vendor serving a combination nobody anticipated —
 * and a vendor that serves five capabilities at once is precisely that — used to get no line at
 * all, silently, which is the same class of gap the label map above turns into a build error but
 * which no type can catch here because the keys are combinations rather than members.
 *
 * So the fallback is built from the per-capability descriptions instead of being absent. It is
 * wordier than a written one, which is the correct trade: wordy and true beats nothing.
 */
function groupHint(services: readonly ProviderService[]): string | undefined {
  const written = GROUP_COPY[services.join('+')];
  if (written !== undefined) return written;
  const parts = services.map((service) => SERVICE_COPY[service]).filter((line) => line !== undefined);
  return parts.length === 0 ? undefined : parts.join(' ');
}

/**
 * A field whose value is one of a short list rather than free text.
 *
 * Keyed on `slug.key` because the credential contract carries no notion of a choice — every
 * field is a string, which is right for an API key and wrong for SMTP's encryption mode,
 * where a typo is not a bad credential but a connection that hangs or, worse, one that sends
 * a password in clear. Adding a `choices` type to the shared catalogue would be the real
 * fix; until then the console renders the select and the adapter still validates the string,
 * so a value typed anywhere else is refused rather than guessed at.
 */
const FIELD_CHOICES: Readonly<Record<string, readonly { value: string; label: string }[]>> = {
  'smtp.tlsMode': [
    { value: '', label: 'Automatic: implicit on 465, STARTTLS elsewhere' },
    { value: 'starttls', label: 'STARTTLS: upgrade after connecting (587)' },
    { value: 'implicit', label: 'Implicit TLS: encrypted from the first byte (465)' },
    { value: 'none', label: 'None: clear text, only for a relay on this machine' },
  ],
};

type Filter = 'all' | 'live' | 'attention';

export function ProviderCredentialsPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'providers:write');
  const state = useAsync(() => api.listProviderCredentials(), []);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<string | null>(null);
  /**
   * Which capability sections the operator has opened or closed by hand.
   *
   * Absent means "whatever the default is", which is closed except for a section holding a
   * vendor that needs a key. Sixteen vendors expanded at once is the wall this page was
   * already trying to get away from; the counts on each header carry the state, so a closed
   * section still answers "is anything wrong in here?" without being opened.
   */
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const data = state.data;

  const groups = useMemo(
    () => (data === null ? [] : groupProviders(data.providers.filter((p) => matches(p, query, filter)))),
    [data, query, filter],
  );

  const filtering = query.trim() !== '' || filter !== 'all';
  const isGroupOpen = (group: ProviderGroup): boolean =>
    filtering || (openGroups[group.key] ?? group.providers.some(isAttention));

  /**
   * Save one provider and refresh the board.
   *
   * Deliberately re-throws: the panel owns the failure, because that is where the inline
   * message belongs and where the switch has to be put back to what the server still
   * believes. Reloading rather than patching local state keeps `services[]` — which
   * capability is simulated — honest after a change that flipped one.
   */
  async function saveProvider(
    slug: string,
    body: SetProviderCredentialsRequest & PendingSetProviderServicesRequest,
  ): Promise<void> {
    const updated = await api.setProviderCredentials(slug, body);
    toast.success(`${updated.label} saved`, noteFor(updated));
    setEditing(null);
    state.reload();
  }

  if (state.loading) return <LoadingState label="Loading provider logins…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (data === null) return <ErrorBox message="No provider data." />;

  const needingAttention = data.providers.filter(isAttention).length;
  const open = editing === null ? undefined : data.providers.find((p) => p.slug === editing);

  return (
    <>
      <div className="page-head">
        {/* The same words the sidebar entry and the topbar use. It said "Provider keys" long
            after both were renamed to "Provider logins", and a heading that disagrees with the
            link you followed is worse than a technical one: the reader cannot tell whether
            they arrived. */}
        <h1 className="page-head__title">Provider logins</h1>
        <p className="page-head__sub">
          Your outside vendors and their keys. A vendor only works when it is switched on and every required key is filled in. Keys are hidden once saved, so you will only ever see the last four characters.
          {!canWrite && ' Your role can view this but not change it.'}
        </p>
      </div>

      <SimulationNote data={data} />
      <CapabilityStrip data={data} />
      <EgressPanel />

      <div className="pkeys-toolbar">
        <SearchInput value={query} onChange={setQuery} placeholder="Find a vendor…" />
        <div className="pkeys-filters" role="group" aria-label="Filter vendors">
          <FilterTab current={filter} value="all" onPick={setFilter}>
            All {data.providers.length}
          </FilterTab>
          <FilterTab current={filter} value="live" onPick={setFilter}>
            Live {data.providers.filter((p) => p.live).length}
          </FilterTab>
          {/* Surfaced as its own tab because it is the one state that costs money to miss: a
              vendor switched on whose required key never resolved is a capability the
              operator believes is hot and which is silently running on its stub. */}
          <FilterTab current={filter} value="attention" onPick={setFilter}>
            Needs a key {needingAttention}
          </FilterTab>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="empty">
          <div className="empty__glyph">–</div>
          <div className="empty__title">No vendor matches.</div>
          <div className="empty__hint">Clear the search, or switch back to All.</div>
        </div>
      ) : (
        groups.map((group) => (
          <CapabilityGroup
            key={group.key}
            group={group}
            open={isGroupOpen(group)}
            // While a search or a filter is narrowing the board, the sections are all open
            // and the toggle is inert: a hit hidden inside a collapsed section reads as no
            // hit at all, which is worse than a long page.
            locked={filtering}
            onToggle={() =>
              setOpenGroups((prev) => ({ ...prev, [group.key]: !isGroupOpen(group) }))
            }
          >
            {group.providers.map((provider) => (
              <ProviderRow
                key={provider.slug}
                provider={provider}
                environment={data.environment}
                canWrite={canWrite}
                onOpen={() => setEditing(provider.slug)}
              />
            ))}
          </CapabilityGroup>
        ))
      )}

      {open !== undefined && (
        <ProviderEditor
          // Remounts when the server's copy changes, so the drafts reset to what is now
          // stored rather than to what the operator typed before a save.
          key={`${open.slug}:${open.updatedAt ?? 'never'}:${String(open.enabled)}`}
          provider={open}
          environment={data.environment}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSave={saveProvider}
        />
      )}
    </>
  );
}

function FilterTab({
  current,
  value,
  onPick,
  children,
}: {
  current: Filter;
  value: Filter;
  onPick: (next: Filter) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`pkeys-filter${current === value ? ' pkeys-filter--on' : ''}`}
      aria-pressed={current === value}
      onClick={() => onPick(value)}
    >
      {children}
    </button>
  );
}

/**
 * What happens when nothing live is serving a capability, said once and prominently.
 *
 * ## This used to answer from the environment, and it answered wrongly
 *
 * The note said "outside production a capability with no live provider answers with safe
 * built-in test data" and "in production it refuses". Both sentences were about how Node was
 * started, and neither is how the API behaves any more.
 *
 * The server now decides per capability and tells us which of three things is happening, on
 * `state`. A capability that can move money or decide identity — naira in, naira out, crypto
 * custody, bills, identity — is allowed to answer from the built-in stub **only** when someone
 * named it deliberately in the deployment's settings, in every environment. Otherwise it
 * refuses the call outright. That rule exists because this platform's live deployment does not
 * run with `NODE_ENV=production`, so the old environment test licensed a fake bill delivery and
 * a fake identity verdict on the real host.
 *
 * So the console reads `state` and stops guessing. `consequential` is the API's own marking of
 * which capabilities move money or prove identity, and a stub answering on one of those is the
 * loudest thing this page can say.
 */
function SimulationNote({ data }: { data: ProviderStatusResponse }): JSX.Element {
  const faking = data.services.filter((s) => s.state === 'simulated' && s.consequential);
  const stubbed = data.services.filter((s) => s.state === 'simulated' && !s.consequential);
  const refusing = data.services.filter((s) => s.state === 'unavailable');
  const alarm = faking.length > 0;
  const nameList = (rows: readonly ProviderServiceState[]): string =>
    listOf(rows.map((s) => SERVICE_LABELS[s.service]));

  return (
    <div className={`pcred-note${alarm ? ' pcred-note--prod' : ''}`} role="note">
      <IconAlert size={18} />
      <div>
        {alarm ? (
          <>
            <strong>
              {nameList(faking)} {faking.length === 1 ? 'is answering' : 'are answering'} with
              built-in test data.
            </strong>{' '}
            No real vendor is called, and nothing that appears to happen actually happens: a bill
            reports delivered and gives out a token that will never work, an identity check passes
            without anybody being checked, a payout settles without any money leaving. Customers
            are charged all the same. Give {faking.length === 1 ? 'it' : 'them'} a live vendor
            below, or have whoever runs this deployment take {faking.length === 1 ? 'it' : 'them'}{' '}
            out of test mode so the calls are refused instead.
          </>
        ) : (
          <>
            <strong>
              Anything that moves money or proves identity refuses every call when it has no live
              vendor, rather than pretending.
            </strong>{' '}
            {refusing.length > 0 &&
              `${nameList(refusing)} ${refusing.length === 1 ? 'has no live vendor' : 'have no live vendor'}, so anything that needs ${refusing.length === 1 ? 'it' : 'them'} fails and says so. `}
            {stubbed.length > 0 &&
              `${nameList(stubbed)} ${stubbed.length === 1 ? 'is running' : 'are running'} on built-in test data, which is safe: nothing there can move money or let somebody in.`}
            {refusing.length === 0 &&
              stubbed.length === 0 &&
              'Every capability below has a live vendor.'}
          </>
        )}{' '}
        A vendor that has never been configured here defaults to{' '}
        <strong>{data.defaultMode === 'live' ? 'live' : 'off'}</strong>.
      </div>
    </div>
  );
}

/** `a`, `a and b`, `a, b and c`. */
function listOf(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/**
 * One chip per capability: who is serving it, or what happens because nobody is.
 *
 * Coloured from the API's own `state` rather than from "is anybody live" plus the environment,
 * which is what it used to do. Those two answers differ exactly where it matters most: a naira
 * payout with no vendor *refuses*, and this strip used to paint it as "Simulated" on any host
 * not started as production — telling an operator that money was moving against a stub when in
 * fact nothing was moving at all, or the reverse.
 */
function CapabilityStrip({ data }: { data: ProviderStatusResponse }): JSX.Element {
  return (
    <div className="pcred-strip">
      {data.services.map((s) => {
        const tone = s.state === 'live' ? 'live' : s.state === 'simulated' ? 'sim' : 'refuse';
        return (
          <div
            key={s.service}
            className={`pcred-chip pcred-chip--${tone}`}
            title={
              s.state === 'live'
                ? `${SERVICE_LABELS[s.service]} is served by ${s.live.join(', ')}.`
                : s.state === 'simulated'
                  ? s.consequential
                    ? 'No live vendor. It is answering with built-in test data, so this appears to work and nothing really happens.'
                    : 'No live vendor. It is answering with built-in test data, not a real vendor.'
                  : 'No live vendor. Every call is refused, which is the safe outcome.'
            }
          >
            <span className="pcred-chip__dot" />
            <span className="pcred-chip__name">{SERVICE_LABELS[s.service]}</span>
            <span className="pcred-chip__detail">
              {s.state === 'live'
                ? s.live.join(' · ')
                : s.state === 'simulated'
                  ? s.consequential
                    ? 'Test data, pretends'
                    : 'Test data'
                  : 'Refuses'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * One capability, and the vendors that serve it, behind a heading that can be closed.
 *
 * The heading is the whole point: it names the capability, says how many vendors sit under
 * it and how many are live or waiting on a key, and it says all of that whether or not the
 * section is open. An operator arriving to fix one vendor reads eight headings and opens
 * one, instead of scrolling sixteen expanded panels looking for a name.
 */
function CapabilityGroup({
  group,
  open,
  locked,
  onToggle,
  children,
}: {
  group: ProviderGroup;
  open: boolean;
  locked: boolean;
  onToggle: () => void;
  children: ReactNode;
}): JSX.Element {
  const bodyId = `pcred-group-${group.key}`;
  const live = group.providers.filter((p) => p.live).length;
  const attention = group.providers.filter(isAttention).length;
  const hint = groupHint(group.services);

  return (
    <section className={`pcred-group${open ? ' pcred-group--open' : ''}`}>
      <h2 className="pcred-group__heading">
        <button
          type="button"
          className="pcred-group__toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          disabled={locked}
          onClick={onToggle}
        >
          <span className="pcred-group__chevron" aria-hidden>
            {open ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          </span>
          <span className="pcred-group__title">
            {group.services.map((s) => SERVICE_LABELS[s]).join(' · ')}
          </span>
          <span className="pcred-group__counts">
            {attention > 0 && (
              <Badge tone="amber" dot>
                {attention} needs a key
              </Badge>
            )}
            {live > 0 && (
              <Badge tone="green" dot>
                {live} live
              </Badge>
            )}
            <span className="pcred-group__total">
              {group.providers.length} {group.providers.length === 1 ? 'vendor' : 'vendors'}
            </span>
          </span>
        </button>
      </h2>

      <div className="pcred-group__body" id={bodyId} hidden={!open}>
        {hint !== undefined && <p className="pcred-group__hint">{hint}</p>}
        <div className="pkeys-rows">{children}</div>
      </div>
    </section>
  );
}

/**
 * One vendor, read-only.
 *
 * Deliberately carries no input of its own. The board's job is to be scannable, and every
 * control it holds is a control somebody can hit by accident on a page they opened to *look*
 * at something.
 */
function ProviderRow({
  provider,
  environment,
  canWrite,
  onOpen,
}: {
  provider: ProviderStatus;
  environment: ProviderStatusResponse['environment'];
  canWrite: boolean;
  onOpen: () => void;
}): JSX.Element {
  const resolved = provider.fields.filter((f) => f.source !== 'unset').length;
  const movesMoney = provider.services.some((s) => MONEY_SERVICES.includes(s));
  const warnMoney = provider.live && environment === 'production' && movesMoney;

  return (
    <div className={`pkeys-row pkeys-row--${stateOf(provider)}`}>
      <div className="pkeys-row__main">
        <div className="pkeys-row__name">
          {provider.label}
          <span className="pcred-card__slug mono">{provider.slug}</span>
          {warnMoney && (
            <span className="pkeys-money" title="Real money moves through this vendor.">
              Moves money
            </span>
          )}
        </div>
        <div className="pkeys-row__desc">{provider.description}</div>
        <div className="pcred-services">
          {provider.services.map((s) => (
            <Badge key={s} tone="neutral">
              {SERVICE_LABELS[s]}
            </Badge>
          ))}
        </div>
      </div>

      <div className="pkeys-row__state">
        <StatusBadge provider={provider} />
        <div className="faint pkeys-row__meta">
          {resolved} of {provider.fields.length} {provider.fields.length === 1 ? 'key' : 'keys'} set
          {provider.updatedAt !== null && ` · updated ${relativeTime(provider.updatedAt)}`}
        </div>
      </div>

      <div className="pkeys-row__action">
        <Button size="sm" variant={provider.enabled && !provider.configured ? 'primary' : 'ghost'} onClick={onOpen}>
          {canWrite ? 'Configure' : 'View'}
        </Button>
      </div>
    </div>
  );
}

/**
 * The four states a vendor can be in, as one word.
 *
 * Shared by the badge and by the row's colour rail so the two can never disagree. The rail
 * matters because the badge is at the right-hand end of a wide row: on a 1440px screen the
 * eye is at the vendor's name, three hundred pixels away from the only thing that said
 * whether it was live.
 */
type VendorState = 'live' | 'missing' | 'ready' | 'unset';

function stateOf(provider: ProviderStatus): VendorState {
  if (provider.live) return 'live';
  if (provider.enabled) return 'missing';
  return provider.configured ? 'ready' : 'unset';
}

function StatusBadge({ provider }: { provider: ProviderStatus }): JSX.Element {
  switch (stateOf(provider)) {
    case 'live':
      return (
        <Badge tone="green" dot>
          Live
        </Badge>
      );
    case 'missing':
      return (
        <Badge tone="amber" dot>
          On, key missing
        </Badge>
      );
    // Configured but switched off is a real and useful state — a vendor staged and ready,
    // waiting for someone to say go — and it reads very differently from "not set up".
    case 'ready':
      return <Badge tone="blue">Ready, switched off</Badge>;
    default:
      return <Badge tone="neutral">Not set up</Badge>;
  }
}

/**
 * The focused editor: one vendor, its switch and its fields, in a dialog.
 *
 * Everything that writes lives here. That is the point of the panel — a form only exists
 * while an operator has deliberately opened it for one named vendor, so there is no way to
 * clear a production key while scrolling past it.
 */
function ProviderEditor({
  provider,
  environment,
  canWrite,
  onClose,
  onSave,
}: {
  provider: ProviderStatus;
  environment: ProviderStatusResponse['environment'];
  canWrite: boolean;
  onClose: () => void;
  onSave: (
    slug: string,
    body: SetProviderCredentialsRequest & PendingSetProviderServicesRequest,
  ) => Promise<void>;
}): JSX.Element {
  // A secret's input always starts empty — there is nothing to prefill it with, and that
  // emptiness is what makes "blank means keep" a safe default. A non-secret is
  // configuration the operator must be able to read back, so it prefills.
  const [draft, setDraft] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of provider.fields) initial[field.key] = field.secret ? '' : (field.value ?? '');
    return initial;
  });
  const [cleared, setCleared] = useState<Record<string, boolean>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [enabled, setEnabled] = useState(provider.enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The per-capability switches, when this API has them.
   *
   * `null` is the ordinary answer today and it means "one switch per vendor". The whole block
   * below then renders nothing extra and the panel is exactly the panel it has always been —
   * which is the point: a console build that is ahead of the API must degrade to what the API
   * can actually do rather than draw controls whose writes are silently dropped.
   */
  const serviceStates = readProviderServiceStates(provider);
  const [services, setServices] = useState<Record<string, boolean>>({});

  const enabledChanged = enabled !== provider.enabled;
  const values = editedValues(provider, draft, cleared);
  /**
   * Only the capabilities the operator actually moved.
   *
   * A sparse patch for the same reason `values` is one: this panel saves credentials and
   * switches in a single request, so sending the whole map would let a credential edit
   * re-assert a capability state that somebody else changed while this dialog was open.
   */
  const serviceEdits = editedServices(serviceStates, services);
  const dirty =
    enabledChanged || Object.keys(values).length > 0 || Object.keys(serviceEdits).length > 0;

  const missingLabels = provider.missing.map(
    (key) => provider.fields.find((f) => f.key === key)?.label ?? key,
  );
  const movesMoney = provider.services.some((s) => MONEY_SERVICES.includes(s));
  const warnMoney = enabled && provider.configured && environment === 'production' && movesMoney;

  async function submit(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await onSave(provider.slug, {
        ...(enabledChanged ? { enabled } : {}),
        ...(Object.keys(values).length > 0 ? { values } : {}),
        ...(Object.keys(serviceEdits).length > 0 ? { services: serviceEdits } : {}),
      });
    } catch (err) {
      setError(messageOf(err));
      // The write did not land, so the switches must go back to what the server still holds —
      // a switch left showing the operator's intent would read as applied.
      setEnabled(provider.enabled);
      setServices({});
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      size="wide"
      title={provider.label}
      description={provider.description}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!canWrite || !dirty}
            onClick={() => void submit()}
          >
            Save
          </Button>
        </>
      }
    >
      {warnMoney && (
        <div className="pcred-warn" role="alert">
          <IconAlert size={16} />
          <div>Real money moves through this vendor.</div>
        </div>
      )}

      {provider.enabled && !provider.configured && (
        <div className="error-box pcred-gap">
          <IconAlert size={16} />
          {/* Deliberately says nothing about what the *capability* is doing instead. That is
              not knowable from one vendor's row — it depends on whether any other vendor is
              live and whether this deployment has been put in test mode — and this panel used
              to assert "replying with safe test data", which is the wrong answer for every
              money and identity capability, where the truth is that calls are refused. The
              strip at the top of the page carries the real answer, per capability. */}
          <div>
            Switched on but not live. {missingLabels.join(', ')}{' '}
            {missingLabels.length === 1 ? 'is' : 'are'} still missing, so nothing is being sent to
            this vendor. The row of capabilities at the top of this page says what is happening
            to the work it was meant to do.
          </div>
        </div>
      )}

      <div className="pcred-switch">
        <div>
          <div className="pcred-switch__label">Enabled</div>
          <div className="pcred-switch__hint">
            {enabled
              ? 'The operator switch is on. Real calls go to this vendor once every required key resolves.'
              : 'Off. This vendor is never called, whatever is stored below.'}
          </div>
        </div>
        <Switch
          checked={enabled}
          disabled={!canWrite || saving}
          label={`${enabled ? 'Disable' : 'Enable'} ${provider.label}`}
          onChange={setEnabled}
        />
      </div>

      {serviceStates !== null && (
        <CapabilitySwitches
          provider={provider}
          states={serviceStates}
          draft={services}
          master={enabled}
          canWrite={canWrite}
          saving={saving}
          onChange={(service, on) => setServices((prev) => ({ ...prev, [service]: on }))}
        />
      )}

      <div className="pcred-fields">
        {provider.fields.map((field) => (
          <CredentialField
            key={field.key}
            slug={provider.slug}
            field={field}
            value={draft[field.key] ?? ''}
            cleared={cleared[field.key] === true}
            revealed={revealed[field.key] === true}
            canWrite={canWrite}
            saving={saving}
            onChange={(next) => setDraft((prev) => ({ ...prev, [field.key]: next }))}
            onToggleReveal={() =>
              setRevealed((prev) => ({ ...prev, [field.key]: prev[field.key] !== true }))
            }
            onToggleClear={() => {
              setCleared((prev) => ({ ...prev, [field.key]: prev[field.key] !== true }));
              setDraft((prev) => ({ ...prev, [field.key]: '' }));
            }}
          />
        ))}
      </div>

      <WebhookPanel provider={provider} />

      {error !== null && <ErrorBox message={error} />}

      <div className="faint pcred-audit">
        {provider.updatedAt === null
          ? 'Never configured from the console.'
          : `Last written ${relativeTime(provider.updatedAt)}.`}{' '}
        Every save records which fields changed, never what they became.
      </div>
    </Modal>
  );
}

/**
 * One switch per capability, for a vendor that serves more than one.
 *
 * ## Why this had to exist
 *
 * Every vendor in this catalogue used to serve one or two capabilities that stood or fell
 * together, so one switch per vendor said everything there was to say. A vendor that serves
 * five does not fit that: crypto custody, naira in, naira out, the price feed and firm quotes
 * behind a single boolean means an operator who wants to try the custody has to switch on the
 * naira rails in the same act, on a production deployment, on real money.
 *
 * ## What each switch actually controls, and what it does not
 *
 * Routing already covers three of them and confusing the two controls is the trap here.
 * Payout, collection and biller are *ranked* on Providers &amp; failover, and a vendor nobody
 * ranked is never called whatever this switch says. So for those three this is a second gate
 * rather than the only one, and the note under the group says so — an operator who switches
 * payouts on here and watches nothing change would otherwise conclude the control is broken.
 *
 * Custody, rates and liquidity have no roster to be absent from. For those, this switch is the
 * only control there is.
 *
 * ## The master switch still wins
 *
 * With the vendor off, every capability is off regardless, so the rows grey out and say so
 * rather than pretending a per-capability choice is in force. The alternative — leaving them
 * live-looking under a master switch that is off — is the exact "a switch that changes nothing"
 * failure this page exists to prevent, reproduced one level down.
 */
function CapabilitySwitches({
  provider,
  states,
  draft,
  master,
  canWrite,
  saving,
  onChange,
}: {
  provider: ProviderStatus;
  states: readonly PendingProviderServiceState[];
  draft: Readonly<Record<string, boolean>>;
  master: boolean;
  canWrite: boolean;
  saving: boolean;
  onChange: (service: string, on: boolean) => void;
}): JSX.Element {
  const routed = states.filter((state) => ROUTED_SERVICES.includes(state.service));

  /**
   * Is the operator about to give crypto custody to a vendor that gates it on verification?
   *
   * Read from the **draft** rather than from what is stored, so the warning appears as the
   * switch is moved rather than after the save it is warning about. That is the whole value of
   * it: a consequence explained afterwards is a post-mortem.
   *
   * Both switches have to be on, because the master switch really does win — a per-capability
   * switch under a vendor that is off changes nothing, and warning about it would be the
   * "control that does nothing" failure this page exists to prevent, wearing a warning's
   * clothes.
   */
  const custodyOn = (draft['custody'] ?? states.find((s) => s.service === 'custody')?.enabled) === true;
  const custodyShapeChange = master && custodyOn && custodyRequiresVerification(provider);

  return (
    <section className="pcred-caps" aria-labelledby={`pcred-caps-${provider.slug}`}>
      <h3 className="pcred-caps__title" id={`pcred-caps-${provider.slug}`}>
        What this vendor is allowed to do
      </h3>
      <p className="pcred-caps__note">
        {master
          ? 'Switch off any of these to keep the vendor live for the rest. Each one is independent.'
          : `${provider.label} is switched off above, so none of these is in force yet.`}
      </p>

      <div className="pcred-caps__list">
        {states.map((state) => {
          const on = draft[state.service] ?? state.enabled;
          const label = SERVICE_LABELS[state.service as ProviderService] ?? state.service;
          return (
            <div
              key={state.service}
              className={`pcred-cap${on && master ? ' pcred-cap--on' : ''}`}
            >
              <div className="pcred-cap__main">
                <div className="pcred-cap__label">
                  {label}
                  {/* The state this page exists to make impossible to miss, one level down:
                      switched on and still never called, because a required key is missing. */}
                  {on && master && !state.live && <Badge tone="amber">Not live</Badge>}
                </div>
                <p className="pcred-cap__hint">
                  {SERVICE_COPY[state.service as ProviderService] ?? 'This capability.'}
                </p>
              </div>
              <Switch
                checked={on && master}
                disabled={!canWrite || saving || !master}
                label={`${on ? 'Stop' : 'Allow'} ${provider.label} handling ${label}`}
                onChange={(next) => onChange(state.service, next)}
              />
            </div>
          );
        })}
      </div>

      {routed.length > 0 && (
        <p className="pcred-caps__note">
          {routed.length === 1
            ? `${SERVICE_LABELS[routed[0]?.service as ProviderService] ?? routed[0]?.service} also has to be ranked on Providers & failover. A vendor nobody ranked is never tried, however this switch is set.`
            : 'Naira in and naira out also have to be ranked on Providers & failover. A vendor nobody ranked is never tried, however these switches are set.'}
        </p>
      )}

      {/*
        The one switch on this page that changes what the app *is*.

        Every other control here turns a rail on or off behind screens that stay the same. This
        one does not. A vendor that will not open a customer record until the customer has been
        identity verified issues no deposit address and no naira account until then, so with it
        live the mobile app stops being "sign up, deposit, verify later" and becomes "verify,
        then everything". Different screens, a different first run, and a different answer to
        every support call about a missing account number.

        It is drawn as a warning and drawn *before* the save, because the operator cannot
        discover any of this from the switch itself and the first evidence otherwise is a
        customer on the phone. The second paragraph is the part that is easy to leave out and
        worst to leave out: the change lands on customers who are already signed up, not only on
        new ones.
      */}
      {custodyShapeChange && (
        <div className="pcred-caps__consequence" role="note">
          <strong className="pcred-caps__consequence-title">
            This vendor changes how the app works for customers
          </strong>
          <p className="pcred-caps__note">
            It will not hold crypto for a customer who has not verified their identity, so while
            crypto custody is on here nobody gets a deposit address or a naira account until they
            are verified. The app shows a verify step in place of those screens.
          </p>
          <p className="pcred-caps__note">
            That applies to customers who already have accounts, not only to new sign-ups.
            Anyone unverified loses the deposit screens the moment this is saved, and gets them
            back as soon as they verify. Assets this vendor does not handle also stop being
            tradeable, though customers keep seeing and keeping whatever they already hold.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * The capabilities that are *also* gated by the routing order.
 *
 * Mirrors `PROVIDER_CAPABILITIES` in the contract, which is the set `provider_configs` has a
 * roster for. It is a copy and it is a small one, and the cost of it being wrong is a sentence
 * that over- or under-explains rather than a control that misbehaves.
 */
const ROUTED_SERVICES: readonly string[] = ['payout', 'collection', 'biller'];

/** Only the capabilities whose switch the operator actually moved. */
function editedServices(
  states: readonly PendingProviderServiceState[] | null,
  draft: Readonly<Record<string, boolean>>,
): Record<string, boolean> {
  if (states === null) return {};
  const edits: Record<string, boolean> = {};
  for (const state of states) {
    const next = draft[state.service];
    if (next !== undefined && next !== state.enabled) edits[state.service] = next;
  }
  return edits;
}

/**
 * The callback URL for this vendor, absolute, with the one line saying where it goes.
 *
 * This is here because it is the last unanswered question of every provider setup, and
 * until now the console did not answer it: an operator either asked someone or assembled
 * the URL by hand from a half-remembered host. A webhook pointed at the wrong host does
 * not fail in a way anybody sees — the vendor's delivery log stays green against whatever
 * answers at the address they were given, and deposits simply never credit. So the URL is
 * shown built rather than described, and it is copyable rather than transcribable.
 *
 * **Only when the API really accepts one.** The descriptor comes from the API's own
 * catalogue, which is asserted against the webhook intake's verifier list, so a vendor
 * with no endpoint here gets no URL and no copy button — BuyPower, whose callback carries
 * no signature scheme anyone has published, and Accelerate, whose webhooks are a Payments
 * product feature that is not wired up. Both still get a paragraph, because an operator
 * who finds a webhook field in a vendor dashboard and no guidance here will fill it in.
 */
function WebhookPanel({ provider }: { provider: ProviderStatus }): JSX.Element | null {
  // `?? null` rather than a plain read: an older deployed API predates this field, and the
  // console is built and shipped separately from it. Undefined has to read as "no callback
  // story" rather than crashing the panel that holds the save button.
  const webhook: ProviderWebhook | null = provider.webhook ?? null;
  if (webhook === null) return null;

  const url = webhook.path === null ? null : absoluteWebhookUrl(webhook.path);

  return (
    <section className="pcred-hook" aria-labelledby={`pcred-hook-${provider.slug}`}>
      <h3 className="pcred-hook__title" id={`pcred-hook-${provider.slug}`}>
        {webhook.path === null ? 'No callback URL' : 'Callback URL'}
        {webhook.path !== null && !webhook.verified && (
          <Badge tone="amber">Not verified yet</Badge>
        )}
      </h3>

      {webhook.path !== null && !webhook.verified && (
        <p className="pcred-hook__note">
          We can&apos;t yet confirm messages from this provider are genuine.
        </p>
      )}

      {webhook.path !== null &&
        (url === null ? (
          // The mock transport has no deployment behind it, so there is no origin that
          // would make an absolute URL true. Showing the path alone would invite someone
          // to prefix it with a guess, which is the failure this panel exists to prevent.
          <p className="pcred-hook__note">
            This console is running on its in-memory demo backend, so there is no deployment
            to build a callback URL against. The path is{' '}
            <code className="mono">
              {API_PREFIX}
              {webhook.path}
            </code>{' '}
            on whichever API a real build points at.
          </p>
        ) : (
          <div className="pcred-hook__url">
            <code className="mono pcred-hook__value">{url}</code>
            <CopyButton value={url} label={`the ${provider.label} callback URL`} />
          </div>
        ))}

      {webhook.dashboard !== null && <p className="pcred-hook__where">{webhook.dashboard}</p>}

      {webhook.warning !== null && (
        <p className="pcred-hook__warn">
          <IconAlert size={15} />
          <span>{webhook.warning}</span>
        </p>
      )}
    </section>
  );
}

function CredentialField({
  slug,
  field,
  value,
  cleared,
  revealed,
  canWrite,
  saving,
  onChange,
  onToggleReveal,
  onToggleClear,
}: {
  slug: string;
  field: ProviderCredentialField;
  value: string;
  cleared: boolean;
  revealed: boolean;
  canWrite: boolean;
  saving: boolean;
  onChange: (next: string) => void;
  onToggleReveal: () => void;
  onToggleClear: () => void;
}): JSX.Element {
  const id = `pcred-${slug}-${field.key}`;
  const hasStoredSecret = field.secret && field.source === 'console' && field.masked !== null;
  const choices = FIELD_CHOICES[`${slug}.${field.key}`];

  return (
    <div className={`pcred-field${cleared ? ' pcred-field--cleared' : ''}`}>
      <div className="pcred-field__head">
        <label className="pcred-field__label" htmlFor={id}>
          {field.label}
          {field.required && <span className="pcred-field__req">required</span>}
        </label>
        <span className={`pcred-field__src pcred-field__src--${field.source}`}>
          {sourceLabel(field)}
        </span>
      </div>

      <p className="pcred-field__hint">{field.hint}</p>

      <div className="pcred-field__row">
        <div className="pcred-field__input">
          {choices === undefined ? (
            <SecretInput
              id={id}
              /*
               * Every field here, not only the secret ones.
               *
               * A base URL is not a password and no manager would offer to fill it with one,
               * but it is still a setting that belongs to the business rather than to the
               * person typing, and the rule is easier to keep when it has no exceptions. The
               * cost is nothing: these are values pasted from a vendor dashboard, and none of
               * them is a thing a browser could usefully remember.
               */
              fieldName={`${slug}-${field.key}`}
              // A secret is masked in the DOM as well as on the wire; the toggle only ever
              // reveals what the operator has typed in this session, never a stored value.
              type={field.secret && !revealed ? 'password' : 'text'}
              value={value}
              disabled={!canWrite || saving}
              placeholder={placeholderFor(field, cleared)}
              onChange={(e) => onChange(e.target.value)}
            />
          ) : (
            <select
              id={id}
              className="select"
              value={value}
              disabled={!canWrite || saving}
              onChange={(e) => onChange(e.target.value)}
            >
              {choices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          )}
        </div>
        {field.secret && (
          <button
            type="button"
            className="pcred-reveal"
            aria-pressed={revealed}
            aria-label={revealed ? `Hide ${field.label}` : `Show ${field.label}`}
            title={revealed ? 'Hide what you typed' : 'Show what you typed'}
            onClick={onToggleReveal}
          >
            <IconEye size={15} />
          </button>
        )}
      </div>

      <div className="pcred-field__foot">
        <span className="pcred-field__note">{noteForField(field, cleared)}</span>
        {(hasStoredSecret || cleared) && (
          <button
            type="button"
            className="pcred-linkbtn"
            disabled={!canWrite || saving}
            onClick={onToggleClear}
          >
            {cleared ? 'Keep stored value' : 'Clear'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────────────

/**
 * The absolute callback URL for a path, or null when there is no API to build one against.
 *
 * Derived from the origin this console actually reaches — not from a hardcoded host and
 * not from `window.location.origin`, because the two are not the same deployment: the API
 * is served at `build.genxpay.com.ng` and this console at `genadmin.genxpay.com.ng`. A URL
 * built from the page's own origin would name the console, which serves no webhook route,
 * and every callback would 404 against a host that looks superficially right.
 *
 * The empty-base case is the console served next to its API on one origin, where the
 * page's origin genuinely is the API's. `USING_MOCK` is the case where there is no API at
 * all, and null is the honest answer rather than a plausible-looking guess.
 *
 * One caveat worth knowing, and it cannot be fixed from here: `VITE_API_BASE_URL` is
 * inlined at build time and is the origin the *browser* reaches. If a deployment ever puts
 * the API behind a proxy whose public hostname differs from the one vendors must call, this
 * URL would be right for the console and wrong for the vendor. Making that impossible needs
 * the API to report its own public origin; today the two are the same host and this is
 * exact.
 */
function absoluteWebhookUrl(path: string): string | null {
  if (USING_MOCK) return null;
  const origin = API_BASE_URL === '' ? window.location.origin : API_BASE_URL;
  return `${origin}${API_PREFIX}${path}`;
}

interface ProviderGroup {
  readonly key: string;
  readonly services: readonly ProviderService[];
  readonly providers: ProviderStatus[];
}

/** Switched on, and still missing something it needs. The one state worth chasing. */
function isAttention(provider: ProviderStatus): boolean {
  return provider.enabled && !provider.configured;
}

function matches(provider: ProviderStatus, query: string, filter: Filter): boolean {
  if (filter === 'live' && !provider.live) return false;
  if (filter === 'attention' && !isAttention(provider)) return false;
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return (
    provider.label.toLowerCase().includes(needle) ||
    provider.slug.toLowerCase().includes(needle) ||
    provider.services.some((s) => SERVICE_LABELS[s].toLowerCase().includes(needle))
  );
}

/**
 * Group the rows by the capability set each vendor serves.
 *
 * Keyed on the whole service list rather than on a single "primary" capability, so a
 * vendor that does both payout and collection appears exactly once, under a header naming
 * both, instead of twice under two headers or once under a header that is half a lie.
 * Insertion order is the API's catalog order, which is also the routing order.
 */
function groupProviders(providers: readonly ProviderStatus[]): ProviderGroup[] {
  const groups = new Map<
    string,
    { key: string; services: readonly ProviderService[]; providers: ProviderStatus[] }
  >();
  for (const provider of providers) {
    const key = provider.services.join('+');
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, { key, services: provider.services, providers: [provider] });
    } else {
      existing.providers.push(provider);
    }
  }
  return [...groups.values()];
}

/**
 * The sparse patch: only what the operator actually changed.
 *
 * A secret is sent only when something was typed into it, or when it was explicitly
 * staged to clear (as `''`). A non-secret is sent when it differs from what came back,
 * which makes emptying one a clear — correct, because a non-secret's input shows its real
 * current value, so an empty box genuinely is a request for no value.
 */
function editedValues(
  provider: ProviderStatus,
  draft: Readonly<Record<string, string>>,
  cleared: Readonly<Record<string, boolean>>,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of provider.fields) {
    const typed = draft[field.key] ?? '';
    if (field.secret) {
      if (cleared[field.key] === true) values[field.key] = '';
      else if (typed.trim() !== '') values[field.key] = typed;
      continue;
    }
    const stored = field.value ?? '';
    if (typed !== stored) values[field.key] = typed;
  }
  return values;
}

function sourceLabel(field: ProviderCredentialField): string {
  if (field.source === 'console') return 'Set in console';
  if (field.source === 'env') return `From environment (${field.envVar ?? 'unnamed'})`;
  return 'Not set';
}

function placeholderFor(field: ProviderCredentialField, cleared: boolean): string {
  if (cleared) return 'Will be removed on save';
  if (field.secret) return field.masked ?? 'Paste the value from the vendor dashboard';
  return field.envVar === null ? '' : 'Blank uses the default';
}

function noteForField(field: ProviderCredentialField, cleared: boolean): string {
  if (cleared) return 'Staged to clear. Save to remove the stored value.';
  if (field.secret) {
    if (field.source === 'console') return 'A value is stored. Leave blank to keep it.';
    if (field.source === 'env') {
      return `Coming from ${field.envVar ?? 'the environment'}. Anything you set here wins over it.`;
    }
    return 'Nothing stored yet.';
  }
  if (field.source === 'env') {
    return `Coming from ${field.envVar ?? 'the environment'}. Anything you set here wins over it.`;
  }
  return field.source === 'console' ? 'Set from this console.' : 'Not set.';
}

/** The one-line consequence of a save, for the toast. */
function noteFor(provider: ProviderStatus): string {
  if (provider.live) return 'Live. Real calls now go to this vendor.';
  if (provider.enabled) {
    const labels = provider.missing.map(
      (key) => provider.fields.find((f) => f.key === key)?.label ?? key,
    );
    return `Switched on but not live yet. Still missing ${labels.join(', ')}.`;
  }
  /*
   * What this vendor is doing, and nothing about what the capability does instead.
   *
   * It used to end "This capability keeps replying with safe test data", which is the opposite
   * of the truth for every capability that moves money or proves identity: with no live vendor
   * those refuse the call. Saying "test data" to an operator who has just switched a payout
   * rail off tells them money is quietly moving against a stub when in fact nothing is moving.
   */
  return 'Switched off. Nothing is sent to this vendor. Check the capabilities at the top of the page for what that leaves.';
}

/**
 * What address this deployment calls vendors from, and whether one of them accepts it.
 *
 * ## Why this panel exists on this page
 *
 * Some vendors gate their API on an allowlist of source addresses, and getting that entry
 * wrong is indistinguishable from getting a *key* wrong: the calls are refused, the console
 * says the vendor is live, and nothing anywhere names the reason. This page is where somebody
 * comes when a vendor is not working, so this is where the answer belongs.
 *
 * ## Why the address is not simply written down somewhere
 *
 * Because it is not knowable from a hosting panel. Three addresses are in play: the ones DNS
 * answers on, the one the host calls out from, and the IPv6 alongside it - and which of those
 * a vendor sees is decided per program, not per machine. An operator reading "my server's IP"
 * out of a control panel gets the wrong one of the three, pastes it in, and is refused with no
 * explanation.
 *
 * ## Why it is a button rather than a live tile
 *
 * It costs two outbound calls, one of them to a vendor. That is fine when somebody is
 * diagnosing and wasteful on every page load, so it stays behind a deliberate press.
 */
function EgressPanel(): JSX.Element {
  const { api } = useAuth();
  const [state, setState] = useState<ProviderEgress | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setState(await api.getProviderEgress());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pkeys-egress" style={{ margin: '12px 0' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void run()}>
          {busy ? 'Checking…' : 'Check outbound address'}
        </button>
        {state !== null && (
          <code style={{ fontSize: 13 }}>{state.address ?? 'could not be determined'}</code>
        )}
        {state !== null && (
          <span className={state.custodian.state === 'allowed' ? 'badge badge--ok' : 'badge'}>
            {state.custodian.state === 'allowed'
              ? 'Custodian accepts this address'
              : state.custodian.state === 'blocked'
                ? 'Custodian is blocking this address'
                : 'Custodian not checked'}
          </span>
        )}
      </div>

      {error !== null && <ErrorBox message={error} />}

      {state !== null && (
        <p className="muted" style={{ marginTop: 6 }}>
          {state.custodian.detail ??
            'This is the address vendors see you arrive from. It is not the address your site answers on, and not the one in your hosting panel. Paste this one into a vendor allowlist.'}
          {state.family === 'verbatim' &&
            ' Outbound calls are not pinned to IPv4 on this deployment, so the address above can change between calls.'}
        </p>
      )}
    </div>
  );
}
